import { ReactElement, useEffect, useState, useCallback } from 'react';

import { Timestamp } from 'firebase/firestore';

import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import FileUploadIcon from '@mui/icons-material/FileUpload';

import dayjs from 'dayjs';

import Papa from 'papaparse';

import { useData } from '../../contextProviders/DataProvider';
import Course, { CoursePair } from '../../types/Course';
import DefaultObjects from '../../types/DefaultObjects';
import Event, { EVENT_TEAM_DEFINED_BY_TYPES, PARTNERSHIP_DEFINED_BY_TYPES } from '../../types/Event';
import EventDay from '../../types/EventDay';
import FirestoreDataTypes from '../../types/FirestoreDataTypes';
import League from '../../types/League';
import Season from '../../types/Season';
import { convertStringToType } from '../../utils/utils';
import { parseDateFlexible, parseNumHoles, parseTimeFlexible, parseWhich9, readSpreadsheetFile, SPREADSHEET_FILE_ACCEPT } from '../../utils/utilsUploads';
import HiddenInput from '../common/HiddenInput';

// Raw CSV record — all values are strings. Includes virtual fields prefixed with _.
interface CsvEventRecord {
  [key: string]: string | undefined;
}

type ParseFailure = {
  row: number;
  data: Partial<CsvEventRecord>;
  error: string;
};

interface EventFileUploadProps {
  league: League;
  season?: Season;
  courses: Course[];
  mapping: Array<{ field: string; dbname: keyof Event }>;
  onEventsLoaded: (events: Event[], failures?: ParseFailure[]) => void;
  hasImportAction?: boolean;
  resetKey?: number;
}

interface EventDayWithCourseDetails extends EventDay {
  courseName?: string;
  courseShortName?: string;
}

// ── Course matching ────────────────────────────────────────────────

/**
 * Match a free-text location string to a Course in the database.
 * Matching order: exact name → exact shortName → startsWith name → contains name/shortName.
 * When multiple results match, prefer courses belonging to the given orgId.
 */
function matchCourse(location: string, courses: Course[], orgId: string): Course | null {
  if (!location || courses.length === 0) return null;
  const loc = location.trim().toLowerCase();
  if (!loc) return null;

  const prefer = (matches: Course[]): Course | null => {
    if (matches.length === 0) return null;
    // Prefer courses in the league's org
    const orgMatch = matches.find((c) => c.orgId === orgId);
    return orgMatch ?? matches[0];
  };

  // 1. Exact match on name
  const exactName = courses.filter((c) => c.name.toLowerCase() === loc);
  if (exactName.length > 0) return prefer(exactName);

  // 2. Exact match on shortName
  const exactShort = courses.filter((c) => c.shortName.toLowerCase() === loc);
  if (exactShort.length > 0) return prefer(exactShort);

  // 3. startsWith on name
  const startsName = courses.filter((c) => c.name.toLowerCase().startsWith(loc));
  if (startsName.length > 0) return prefer(startsName);

  // 4. Contains on name or shortName
  const contains = courses.filter(
    (c) => c.name.toLowerCase().includes(loc) || c.shortName.toLowerCase().includes(loc),
  );
  if (contains.length > 0) return prefer(contains);

  return null;
}

// ── Component ──────────────────────────────────────────────────────

export default function EventFileUpload(props: EventFileUploadProps): ReactElement {
  const { league, season, courses, mapping, onEventsLoaded, hasImportAction = true, resetKey = 0 } = props;
  const { userState } = useData();
  const [csvContent, setCsvContent] = useState<string>('');
  const [parseProgress, setParseProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [failures, setFailures] = useState<ParseFailure[]>([]);

  useEffect(() => {
    if (csvContent && league) {
      parseCsvContents(csvContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvContent, league]);

  useEffect(() => {
    setCsvContent('');
    setParseProgress({ current: 0, total: 0 });
    setIsProcessing(false);
    setFailures([]);
  }, [resetKey]);

  type ParseFailure = {
    row: number;
    data: Partial<CsvEventRecord>;
    error: string;
  };

  // ── Virtual field helpers ──────────────────────────────────────

  /** Check whether a dbname is a virtual field (prefixed with _). */
  const isVirtualField = (dbname: string) => {
    const fieldPart = dbname.includes('.') ? dbname.split('.').pop() : dbname;
    return fieldPart?.startsWith('_');
  };

  /**
   * Post-process virtual fields on each day:
   *  _date + _time → datetime (using course/league timezone)
   *  _location → courseId / socialLocation
   *  numHoles / which9 → validated (18 or 9; front or back)
   * Also resolves booleans & defaults for isGolf, isShotgun.
   */
  const postProcessDay = useCallback((
    day: EventDayWithCourseDetails,
    virtualFields: Record<string, string>,
    rowIndex: number,
    warnings: string[],
  ) => {
    // ── Resolve booleans with defaults ──
    if (day.isGolf === undefined) day.isGolf = true;
    if (day.isShotgun === undefined) day.isShotgun = false;

    // ── Resolve Location ──
    const locationStr = virtualFields['_location']?.trim();
    if (locationStr) {
      if (day.isGolf) {
        const course = matchCourse(locationStr, courses, league.orgId);
        if (course) {
          day.courseId = course.itemId;
          day.courseName = course.name;
          day.courseShortName = course.shortName;
          // Store course timezone for datetime resolution below
          (day as EventDayWithCourseDetails & { _courseTimezone?: string })._courseTimezone = course.timezone;
        } else {
          warnings.push(`Row ${rowIndex + 1}: Could not match location "${locationStr}" to a course`);
        }
      } else {
        // Social event — store as socialLocation
        day.socialLocation = locationStr;
      }
    }

    // ── Resolve Date + Time → datetime ──
    const dateStr = virtualFields['_date'];
    if (dateStr) {
      const parsedDate = parseDateFlexible(dateStr);
      if (parsedDate) {
        // Determine time
        const timeStr = virtualFields['_time'];
        const parsedTime = timeStr ? parseTimeFlexible(timeStr) : null;
        const time = parsedTime ?? league.defaultStartTime ?? { hour: 0, minute: 0 };

        // Determine timezone: course → league default → browser local
        const courseTimezone = (day as EventDayWithCourseDetails & { _courseTimezone?: string })._courseTimezone;
        const tz = courseTimezone
          || league.defaultCoursePair?.timezone
          || Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Build the datetime in the resolved timezone
        const dt = dayjs.tz(
          `${parsedDate.format('YYYY-MM-DD')} ${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`,
          tz,
        );
        day.datetime = Timestamp.fromDate(dt.toDate());

        // Also set event timezone if not yet set
      } else {
        warnings.push(`Row ${rowIndex + 1}: Could not parse date "${dateStr}"`);
      }
    }

    // ── Resolve holes: 18 or 9; a nine-hole day plays the front unless Which 9 says back ──
    // The generic field pass has already stored the raw cell values (numHoles defaults to 18).
    const rawNumHoles = String(day.numHoles ?? '');
    const numHoles = parseNumHoles(rawNumHoles);
    if (numHoles === null) {
      warnings.push(`Row ${rowIndex + 1}: Num Holes "${rawNumHoles}" must be 18 or 9 — using 18`);
    }
    day.numHoles = numHoles ?? 18;

    const rawWhich9 = String(day.which9 ?? '').trim();
    if (day.numHoles === 9) {
      const which9 = rawWhich9 ? parseWhich9(rawWhich9) : 'front';
      if (which9 === null) {
        warnings.push(`Row ${rowIndex + 1}: Which 9 "${rawWhich9}" must be Front or Back — using Front`);
      }
      day.which9 = which9 ?? 'front';
    } else {
      if (rawWhich9) {
        warnings.push(`Row ${rowIndex + 1}: Which 9 "${rawWhich9}" ignored on an 18-hole day`);
      }
      delete day.which9;
    }

    // Clean up transient properties
    delete (day as EventDayWithCourseDetails & { _courseTimezone?: string })._courseTimezone;
  }, [courses, league]);

  // ── Group header stripping ────────────────────────────────────

  /**
   * Detect and strip a group header row from CSV content.
   * Templates may include a group row ("Event", "", "", "Day 1", ...) above
   * the real column headers. PapaParse's `header: true` would mistake the
   * group row for column names, so we strip it when detected.
   *
   * Detection: if the second line contains multiple matches against our
   * mapping field names, the first line is a group header.
   */
  const stripGroupHeaderIfPresent = useCallback((csv: string): string => {
    const lines = csv.split('\n');
    if (lines.length < 2) return csv;

    const normalizeField = (field: string) =>
      field.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const mappedFieldNames = new Set(mapping.map((m) => normalizeField(m.field)));

    const secondLineFields = lines[1].split(',').map((f) => normalizeField(f));
    const matchCount = secondLineFields.filter((f) => f && mappedFieldNames.has(f)).length;

    // If the second line has multiple column name matches, first line is a group header
    if (matchCount >= 2) {
      return lines.slice(1).join('\n');
    }

    return csv;
  }, [mapping]);

  // ── Main parse function ────────────────────────────────────────

  const parseCsvContents = useCallback(
    (contents: string) => {
      const parsedEvents: Event[] = [];
      const parseWarnings: string[] = [];

      if (!league) {
        onEventsLoaded([], [{ row: 0, data: {}, error: 'League not found' }]);
        console.error('League not found');
        return;
      }

      setIsProcessing(true);
      let rowIndex = 0;

      // Strip group header row if present (template downloads include one)
      const processedContents = stripGroupHeaderIfPresent(contents);

      Papa.parse<CsvEventRecord>(processedContents, {
        header: true,
        skipEmptyLines: true,
        delimiter: ',',
        transformHeader: (header) => {
          const normalizeField = (field: string) =>
            field
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9]/g, '');

          const trimmedHeader = header.trim();
          const mappedField = mapping.find((m) => normalizeField(m.field) === normalizeField(trimmedHeader));

          return mappedField ? String(mappedField.dbname) : trimmedHeader;
        },
        step: (results) => {
          const row = results.data as CsvEventRecord;

          // Skip rows where all values are empty (Excel blank rows produce comma-only CSV lines)
          const hasData = Object.values(row).some((v) => v !== undefined && String(v).trim() !== '');
          if (!hasData) return;

          // Initialize event with basic fields
          const event = {
            ...DefaultObjects.Event,
            creatorId: userState.userProfile?.itemId || 'system',
            createTime: Timestamp.now(),
            orgId: league.orgId,
            orgName: league.orgName,
            ...(season && { seasonId: season.itemId, seasonName: season.name }),
            leagueId: league.itemId,
            leagueName: league.name,
          } as Event;

          // Track which day indices actually have data and collect virtual fields per day
          const daysWithData = new Set<number>();
          const dayVirtualFields = new Map<number, Record<string, string>>();

          // First pass: identify which days have data and collect virtual field values
          Object.keys(row).forEach((key) => {
            const value = row[key];
            if (value === undefined || String(value).trim() === '') return;

            const dayFieldMatch = key.match(/^days\[(\d+)\]\./);
            if (dayFieldMatch) {
              const dayIndex = parseInt(dayFieldMatch[1]);
              daysWithData.add(dayIndex);

              const fieldName = key.split('.')[1];
              if (fieldName.startsWith('_')) {
                if (!dayVirtualFields.has(dayIndex)) dayVirtualFields.set(dayIndex, {});
                dayVirtualFields.get(dayIndex)![fieldName] = String(value).trim();
              }
            }
          });

          // Initialize days array only for days that have data
          if (daysWithData.size > 0) {
            event.days = [];
            const sortedDayIndices = Array.from(daysWithData).sort((a, b) => a - b);
            sortedDayIndices.forEach((dayIndex) => {
              while (event.days.length <= dayIndex) {
                event.days.push({ ...DefaultObjects.Event.days[0] });
              }
            });
          } else {
            event.days = [{ ...DefaultObjects.Event.days[0] }];
          }

          // Only process fields that are in our mapping (skip virtual fields)
          const mappedFields = new Set(mapping.map((m) => String(m.dbname)));

          (Object.keys(row)).forEach((key) => {
            const rawValue = row[key];
            if (rawValue === undefined || String(rawValue).trim() === '') return;
            const value = String(rawValue);

            // Skip virtual fields — they are post-processed separately
            if (isVirtualField(key)) return;

            const dayFieldMatch = key.match(/^days\[(\d+)\]\./);
            if (dayFieldMatch) {
              const dayIndex = parseInt(dayFieldMatch[1]);
              const fieldName = key.split('.')[1] as keyof EventDay;

              if (dayIndex < event.days.length) {
                (event.days[dayIndex][fieldName] as FirestoreDataTypes) = convertStringToType(
                  value.trim(),
                  fieldName,
                  event.days[dayIndex]
                );
              }
            } else if (mappedFields.has(String(key))) {
              if ((key as string).includes('.') && !(key as string).includes('[')) {
                // Dot-notation nested field (e.g. 'teamStructure.size') — build nested object
                const [parent, child] = (key as string).split('.');
                if (parent && child) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (event as any)[parent] ??= {};
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (event as any)[parent][child] = convertStringToType(value.trim(), child, (event as any)[parent]);
                }
              } else {
                (event[key as keyof Event] as FirestoreDataTypes) = convertStringToType(value.trim(), key, event, key === 'signupClosesAt' ? '23:59:59' : undefined);
              }
            }
          });

          // Post-process virtual fields per day (Location, Date, Time)
          event.days.forEach((day, dayIndex) => {
            const virtuals = dayVirtualFields.get(dayIndex) ?? {};
            postProcessDay(day as EventDayWithCourseDetails, virtuals, rowIndex, parseWarnings);
          });

          // Set event timezone from first day's course if not already set
          if (!event.timezone || event.timezone === Intl.DateTimeFormat().resolvedOptions().timeZone) {
            const firstDayCourse = event.days[0]?.courseId
              ? courses.find((c) => c.itemId === event.days[0].courseId)
              : null;
            if (firstDayCourse?.timezone) {
              event.timezone = firstDayCourse.timezone;
            } else if (league.defaultCoursePair?.timezone) {
              event.timezone = league.defaultCoursePair.timezone;
            }
          }

          // Apply league defaults for remaining missing values
          applyLeagueDefaults(event, league);

          // Auto-calculate numTeeTimes from maxPlayers if not set
          if (event.numTeeTimes === DefaultObjects.Event.numTeeTimes && event.maxPlayers > 0) {
            event.numTeeTimes = Math.ceil(event.maxPlayers / (event.teeSheetGroupSize ?? 4));
          }

          // Fix up course information and compute startDatetime
          fixupEventCoursePairs(event);

          // Apply signup window defaults (needs startDatetime from fixup)
          applySignupDefaults(event, league);

          setParseProgress((prev) => ({
            current: prev.current + 1,
            total: prev.total + 1,
          }));

          parsedEvents.push(event);
          rowIndex++;

          return event;
        },
        complete: () => {
          setIsProcessing(false);
          setParseProgress({ current: 0, total: 0 });
          if (parseWarnings.length > 0) {
            console.warn('Parse warnings:', parseWarnings);
          }
          onEventsLoaded(parsedEvents, failures);
        },
        error: (error: Error) => {
          console.error('Error parsing CSV:', error);
          setIsProcessing(false);
          setParseProgress({ current: 0, total: 0 });
          onEventsLoaded([], [{ row: 0, data: {}, error: error.message }]);
        },
      });
    },
    [league, userState.userProfile, mapping, onEventsLoaded, failures, season, courses, postProcessDay, stripGroupHeaderIfPresent]
  );

  // ── League defaults ────────────────────────────────────────────

  /**
   * Apply league defaults to an event when values are not provided.
   * - Default course pair applied to any day missing a courseId
   * - Default start time applied to any day whose datetime has no time component
   * - Default max players, timezone applied at event level
   * - Day-level defaults: isGolf true, isShotgun false, isPractice false, numHoles 18
   */
  const applyLeagueDefaults = (event: Event, league: League) => {
    // Day-level defaults
    event.days.forEach((day) => {
      // Apply default course pair if day has no course and is a golf event
      if (!day.courseId && day.isGolf !== false && league.defaultCoursePair) {
        day.courseId = league.defaultCoursePair.id;
        const dayWithDetails = day as EventDayWithCourseDetails;
        dayWithDetails.courseName = league.defaultCoursePair.name;
        dayWithDetails.courseShortName = league.defaultCoursePair.shortName;
      }

      // Apply default start time if the day's datetime has no time (midnight)
      if (day.datetime instanceof Timestamp && league.defaultStartTime) {
        const dt = dayjs(day.datetime.toDate());
        if (dt.hour() === 0 && dt.minute() === 0) {
          const withTime = dt
            .hour(league.defaultStartTime.hour)
            .minute(league.defaultStartTime.minute);
          day.datetime = Timestamp.fromDate(withTime.toDate());
        }
      }

      // Boolean defaults
      if (day.isGolf === undefined) day.isGolf = true;
      if (day.isShotgun === undefined) day.isShotgun = false;
      if (day.isPractice === undefined) day.isPractice = false;

      // Default numHoles to 18 if not set
      if (day.numHoles === undefined) day.numHoles = 18;
    });

    // Event-level defaults
    if (league.defaultMaxPlayers && event.maxPlayers === DefaultObjects.Event.maxPlayers) {
      event.maxPlayers = league.defaultMaxPlayers;
    }
    if (league.defaultTeeSheetGroupSize && event.teeSheetGroupSize === undefined) {
      event.teeSheetGroupSize = league.defaultTeeSheetGroupSize;
    }

    // When teamStructure was built from dot-notation CSV fields, fill in required defaults
    if (event.teamStructure) {
      if (!event.teamStructure.teamDefinedBy) {
        event.teamStructure.teamDefinedBy = event.teamStructure.areTeamsSetByAdmin
          ? EVENT_TEAM_DEFINED_BY_TYPES.ADMIN
          : EVENT_TEAM_DEFINED_BY_TYPES.PLAYER;
      }
      if (!event.isTeamEvent) {
        event.isTeamEvent = true;
      }
      // Dual-written during the teamStructure -> partnership migration (see EventPartnership's own
      // deprecation note) — CSV-imported events get both shapes just like every other write path.
      event.isPartnershipEvent = true;
      event.partnership = {
        definedBy: event.teamStructure.teamDefinedBy === EVENT_TEAM_DEFINED_BY_TYPES.ADMIN
          ? PARTNERSHIP_DEFINED_BY_TYPES.ADMIN
          : PARTNERSHIP_DEFINED_BY_TYPES.PLAYER,
        memberGuest: event.teamStructure.memberGuest,
        size: event.teamStructure.size,
        maxGuests: event.teamStructure.maxGuests,
      };
    }

    if (league.defaultCoursePair?.timezone && !event.timezone) {
      event.timezone = league.defaultCoursePair.timezone;
    }

    if (league.color && !event.color) {
      event.color = league.color;
    }
  };

  // ── Signup defaults ────────────────────────────────────────────

  /**
   * Apply signup window defaults relative to the event's computed startDatetime.
   * Must be called after fixupEventCoursePairs (which sets startDatetime).
   */
  const applySignupDefaults = (event: Event, league: League) => {
    if (!(event.startDatetime instanceof Timestamp)) return;

    const eventStart = dayjs(event.startDatetime.toDate());

    const defaultSignupOpens = DefaultObjects.Event.signupOpensAt;
    if (league.defaultEventSignupOpensDaysPrior !== undefined &&
        event.signupOpensAt instanceof Timestamp &&
        defaultSignupOpens instanceof Timestamp &&
        event.signupOpensAt.toMillis() === defaultSignupOpens.toMillis()) {
      event.signupOpensAt = Timestamp.fromDate(
        eventStart.subtract(league.defaultEventSignupOpensDaysPrior, 'day').toDate()
      );
    }

    const defaultSignupCloses = DefaultObjects.Event.signupClosesAt;
    if (league.defaultEventSignupClosesDaysPrior !== undefined &&
        event.signupClosesAt instanceof Timestamp &&
        defaultSignupCloses instanceof Timestamp &&
        event.signupClosesAt.toMillis() === defaultSignupCloses.toMillis()) {
      event.signupClosesAt = Timestamp.fromDate(
        eventStart.subtract(league.defaultEventSignupClosesDaysPrior, 'day').endOf('day').toDate()
      );
    }
  };

  // ── Course pairs fixup ─────────────────────────────────────────

  const fixupEventCoursePairs = (event: Event) => {
    if (!event.coursePairs) {
      event.coursePairs = [];
    }

    const courseMap = new Map<string, CoursePair>();

    event.coursePairs.forEach((course) => {
      if (course.id) courseMap.set(course.id, course);
    });

    event.days.forEach((day) => {
      if (day.courseId && !courseMap.has(day.courseId)) {
        const dayWithDetails = day as EventDayWithCourseDetails;
        const coursePair: CoursePair = {
          id: day.courseId,
          name: dayWithDetails.courseName || 'Unknown Course',
          shortName: dayWithDetails.courseShortName || 'Unknown',
          timezone: event.timezone || 'America/Los_Angeles',
        };
        courseMap.set(day.courseId, coursePair);
      }
    });

    event.coursePairs = Array.from(courseMap.values());

    // Compute startDatetime from earliest day
    if (event.days.length > 0) {
      let earliestTime: Timestamp | null = null;
      for (const day of event.days) {
        if (day.datetime instanceof Timestamp) {
          if (earliestTime === null || day.datetime.toMillis() < earliestTime.toMillis()) {
            earliestTime = day.datetime;
          }
        }
      }
      if (earliestTime) {
        event.startDatetime = earliestTime;
      }
    }
  };

  // ── File upload handler ────────────────────────────────────────

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setCsvContent(await readSpreadsheetFile(file));
    } catch (error) {
      console.error('Error reading file', error);
    }
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        display: "flex",
        justifyContent: "center"
      }}>
      {hasImportAction && (
        <Button
          component="label"
          role={undefined}
          size="small"
          variant="contained"
          tabIndex={-1}
          startIcon={<FileUploadIcon />}
          disabled={isProcessing ?? !league}
        >
          Import CSV or XLSX file
          <HiddenInput type="file" accept={SPREADSHEET_FILE_ACCEPT} onChange={handleFileUpload} />
        </Button>
      )}
      {isProcessing && (
        <>
          <Typography variant="bodyMedium" align="center" sx={{
            color: "text.secondary"
          }}>
            Processing row {parseProgress.current} of {parseProgress.total}
          </Typography>
          <LinearProgress variant="determinate" value={(parseProgress.current / parseProgress.total) * 100} />
        </>
      )}
    </Stack>
  );
}
